import { clsx } from 'clsx';
import { type KeyboardEvent, type ReactNode, useId, useRef } from 'react';

/**
 * Tabs with the ARIA tabs pattern: roving tabindex, arrow keys, Home and End. The active tab
 * is a butter plate pressed into place (no shadow), the others stand up with a shadow, so the
 * state reads without color.
 *
 *   const [tab, setTab] = useState('overview');
 *   <Tabs idBase="profile" label="Profile" value={tab} onChange={setTab}
 *         items={[{ id: 'overview', label: 'Overview' }, { id: 'games', label: 'Games', count: 12 }]} />
 *   <div {...tabPanelProps('profile', tab)}>...</div>
 */
export interface TabItem<T extends string = string> {
  id: T;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
}

export interface TabsProps<T extends string = string> {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** Accessible name of the tab list. */
  label: string;
  /** Prefix for tab and panel ids; pass the same to tabPanelProps. */
  idBase?: string;
  /** Stretch tabs to fill the row. */
  fill?: boolean;
  className?: string;
}

export function tabId(idBase: string, id: string): string {
  return `${idBase}-tab-${id}`;
}

export function tabPanelProps(idBase: string, id: string) {
  return {
    id: `${idBase}-panel-${id}`,
    role: 'tabpanel' as const,
    'aria-labelledby': tabId(idBase, id),
    tabIndex: 0,
  };
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  idBase,
  fill = false,
  className,
}: TabsProps<T>) {
  const auto = useId();
  const base = idBase ?? auto;
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const enabled = items.map((t, i) => (t.disabled ? -1 : i)).filter((i) => i >= 0);

  const onKey = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const pos = enabled.indexOf(index);
    let next: number | undefined;
    if (e.key === 'ArrowRight') next = enabled[(pos + 1) % enabled.length];
    else if (e.key === 'ArrowLeft') next = enabled[(pos - 1 + enabled.length) % enabled.length];
    else if (e.key === 'Home') next = enabled[0];
    else if (e.key === 'End') next = enabled[enabled.length - 1];
    if (next === undefined) return;
    e.preventDefault();
    const item = items[next];
    if (!item) return;
    onChange(item.id);
    refs.current[next]?.focus();
  };

  return (
    <div role="tablist" aria-label={label} className={clsx('flex gap-2 overflow-x-auto px-1 pt-1 pb-2', className)}>
      {items.map((t, i) => {
        const selected = t.id === value;
        return (
          <button
            key={t.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={tabId(base, t.id)}
            aria-selected={selected}
            aria-controls={`${base}-panel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            disabled={t.disabled}
            onClick={() => onChange(t.id)}
            onKeyDown={(e) => onKey(e, i)}
            className={clsx(
              'inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 rounded-tts border-(length:--tts-border) border-ink px-3.5 font-display text-sm font-black uppercase italic tracking-wide text-ink transition-transform duration-(--dur-instant) disabled:opacity-50',
              fill && 'flex-1',
              selected
                ? 'translate-x-[3px] translate-y-[3px] bg-butter'
                : 'bg-white shadow-[3px_3px_0_0_var(--color-ink)]',
            )}
          >
            {t.label}
            {t.count !== undefined && (
              <span className="rounded-chip border-2 border-ink bg-white px-1 font-sans text-xs not-italic">
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
